import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { IdentityProviderData } from './types';
import axios from 'server';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { Switcher } from 'components/Switcher';
const cardStyle = {
  background: '#fff',
  border: '1px solid #e6eaf0',
  borderRadius: 12,
  boxShadow: '0 3px 12px rgba(35, 45, 65, 0.06)',
  padding: '22px 28px',
};

const sectionButtonStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '18px 28px',
  textTransform: 'none' as const,
  color: '#202b3c',
  fontWeight: 600,
  fontSize: 16,
};

const readonlyFieldStyle = {
  backgroundColor: '#f7f9fc',
  borderRadius: 6,
};

const ProviderTable: FC = () => {
  const [selectedProvider, setSelectedProvider] = useState<IdentityProviderData | null>(null);
  const [validityDays, setValidityDays] = useState(365);
  const [regenSigningAlgo, setRegenSigningAlgo] = useState('');
  const [regenDigestAlgo, setRegenDigestAlgo] = useState('');
  const [showCertificateDetails, setShowCertificateDetails] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);

  const {
    notificationStore: { openNotification },
  } = useStore();

  const regenerateCert = useMutation(
    () => {
      const payload: any = { validity_days: validityDays };
      if (regenSigningAlgo) payload.signing_algorithm = regenSigningAlgo;
      if (regenDigestAlgo) payload.digest_algorithm = regenDigestAlgo;
      return axios.post('saml/idp/certificate/regenerate', payload);
    },
    {
      onSuccess: () => {
        openNotification(
          'success',
          'Certificate regenerated successfully. Please update the new certificate in your connected Service Provider.'
        );
      },
      onError: (error: any) => {
        openNotification(
          'error',
          error?.response?.data?.message || 'The validity days field must be between 1 and 3650.'
        );
      },
    }
  );

  const fetchProviders = async (): Promise<IdentityProviderData[]> => {
    const response = await axios.get('saml/idp-details');
    const raw = response.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (raw?.data && typeof raw.data === 'object' && raw.data.idp_name) return [raw.data];
    if (Array.isArray(raw?.items)) return raw.items;
    if (raw && typeof raw === 'object' && raw.idp_name) return [raw];
    return [];
  };

  const {
    data: providerList = [],
    isLoading: isFetchingProvider,
    isFetching,
    isError: isProviderError,
  } = useQuery<IdentityProviderData[]>('saml/idp-details', fetchProviders, {
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  useEffect(() => {
    setSelectedProvider(providerList.length > 0 ? providerList[0] : null);
  }, [providerList]);

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return '-';

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return date;

    return parsedDate.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getCertificateAlertThresholds = (provider: IdentityProviderData): string[] => {
    const thresholds = provider.certificate_alert?.thresholds;
    if (!Array.isArray(thresholds)) return [];

    return thresholds.map((threshold: any) => {
      if (typeof threshold === 'object' && threshold !== null) {
        return `${threshold.days ?? threshold.value ?? threshold.threshold ?? ''} days`.trim();
      }
      return `${threshold} days`;
    });
  };

  const getOptionLabel = (value: any): string => {
    if (value && typeof value === 'object') return String(value.label ?? value.value ?? '');
    return String(value ?? '');
  };

  const getBooleanOptionLabel = (value: any): string => {
    const label = getOptionLabel(value);
    if (label) return label;
    return value?.value === true ? 'Enabled' : 'Disabled';
  };

  const copyToClipboard = async (value: string | null | undefined, label: string) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      openNotification('success', `${label} copied to clipboard.`);
    } catch (error) {
      openNotification('error', `Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const formatBinding = (binding: string) =>
    String(binding)
      .replace(/urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect/gi, 'HTTP-Redirect')
      .replace(/urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST/gi, 'HTTP-POST')
      .replace(/HTTP Redirect/gi, 'HTTP-Redirect')
      .replace(/HTTP POST/gi, 'HTTP-POST');

  const getReadinessStatus = (provider: IdentityProviderData) => {
    const alert = provider.certificate_alert;
    const status = String(alert?.status || '').toLowerCase();
    const daysRemaining = alert?.days_remaining;
    const thresholdDays = alert?.threshold_days;

    if (status.includes('expired') || (typeof daysRemaining === 'number' && daysRemaining <= 0)) {
      return {
        label: 'Action needed',
        title: 'Action needed',
        explanation:
          'The signing certificate has expired. Renew it and update the connected Service Provider.',
        type: 'error',
      };
    }

    if (
      status.includes('expir') ||
      (typeof daysRemaining === 'number' &&
        typeof thresholdDays === 'number' &&
        daysRemaining <= thresholdDays)
    ) {
      return {
        label: 'Certificate expiring soon',
        title: 'Certificate expiring soon',
        explanation: `The signing certificate expires in ${daysRemaining} day(s). Plan renewal and update the connected Service Provider after regeneration.`,
        type: 'warning',
      };
    }

    if (!provider.metadata_url || !provider.entity_id || !provider.sso_url) {
      return {
        label: 'Action needed',
        title: 'Action needed',
        explanation: 'One or more required IdP connection details are unavailable.',
        type: 'error',
      };
    }

    return {
      label: 'Ready',
      title: 'Ready to connect applications',
      explanation: 'The SAML identity provider is enabled and its signing certificate is valid.',
      type: 'success',
    };
  };

  const formatCertificate = (certificate: string | null | undefined): string => {
    if (!certificate) return '';
    if (certificate.includes('BEGIN CERTIFICATE')) return certificate;

    const cleanCertificate = certificate
      .replace(/-----BEGIN CERTIFICATE-----/g, '')
      .replace(/-----END CERTIFICATE-----/g, '')
      .replace(/\s/g, '');

    const lines = cleanCertificate.match(/.{1,64}/g);
    if (!lines) return certificate;

    return ['-----BEGIN CERTIFICATE-----', ...lines, '-----END CERTIFICATE-----'].join('\n');
  };

  const handleDownloadCertificate = (provider: IdentityProviderData) => {
    if (provider.certificate_download_url) {
      window.open(provider.certificate_download_url, '_blank');
      return;
    }

    if (provider.x509_certificate) {
      const certificate = formatCertificate(provider.x509_certificate);
      const blob = new Blob([certificate], { type: 'application/x-pem-file' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `${provider.idp_name || 'saml-certificate'}.crt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleRegenerateCertificate = () => {
    const confirmed = window.confirm(
      'Regenerate the SAML signing certificate? This creates a new certificate and connected Service Providers may need to be updated before SSO can continue with the new signing key.'
    );

    if (confirmed) regenerateCert.mutate();
  };

  const readiness = selectedProvider ? getReadinessStatus(selectedProvider) : null;
  const providerWithStatus = selectedProvider as (IdentityProviderData & { is_active?: boolean }) | null;
  const isProviderEnabled = providerWithStatus?.is_active !== false;
  const handleProviderToggle = () => {
    const newStatus = !isProviderEnabled;
    axios.patch('saml/idp-details', { is_active: newStatus })
      .then(() => setSelectedProvider((prev) => prev ? { ...prev, is_active: newStatus } : prev))
      .catch((error) => console.error('Failed to update provider status:', error));
  };


  useAppPreloader(isFetchingProvider || isFetching || regenerateCert.isLoading);

  if (isFetchingProvider) return null;

  if (isProviderError) {
    return (
      <Typography color='error' style={{ padding: 20 }}>
        Failed to load provider applications.
      </Typography>
    );
  }

  if (providerList.length === 0 || !selectedProvider) {
    return <Typography style={{ padding: 20, textAlign: 'center' }}>No Provider Applications found.</Typography>;
  }

  const metadataUrl = selectedProvider.metadata_url || '';
  const entityId = selectedProvider.entity_id || '';
  const ssoUrl = selectedProvider.sso_url || '';
  const certificateExpiry = formatDate(selectedProvider.certificate_valid_until);

  const certificateThresholds = getCertificateAlertThresholds(selectedProvider);
  const certificateStatus = readiness?.type === 'success' ? 'Valid' : readiness?.label || 'Action needed';

  return (

    <>
      <Typography variant='h5' style={{ color: '#202b3c', fontWeight: 700, marginBottom: 8 }}>
        SAML identity provider
      </Typography>

      <Typography variant='body1' style={{ color: '#687891', marginBottom: 22 }}>
        Connection details for applications that use EasyIdentity to sign in.
      </Typography>

      {/* IdP readiness */}
      <Paper
        elevation={0}
        style={{
          ...cardStyle,
          padding: '17px 20px',
          marginBottom: 24,
          background:
            readiness?.type === 'success'
              ? '#e9f9f1'
              : readiness?.type === 'warning'
                ? '#fff8e7'
                : '#fff0f0',
          borderColor:
            readiness?.type === 'success'
              ? '#bfead4'
              : readiness?.type === 'warning'
                ? '#f0d58d'
                : '#efc5c5',
        }}
      >
        <Grid container alignItems='center' spacing={2}>
          <Grid item>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background:
                  readiness?.type === 'success'
                    ? '#d0f2df'
                    : readiness?.type === 'warning'
                      ? '#ffedbf'
                      : '#ffdede',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 700,
              }}
            >
              {readiness?.type === 'success' ? '✓' : readiness?.type === 'warning' ? '!' : '×'}
            </div>
          </Grid>
          <Grid item xs>
            <Typography
              variant='subtitle1'
              style={{
                fontWeight: 700,
                color:
                  readiness?.type === 'success'
                    ? '#087b52'
                    : readiness?.type === 'warning'
                      ? '#9a6900'
                      : '#a33131',
              }}
            >
              {readiness?.title}
            </Typography>
            <Typography variant='body2' style={{ color: '#687891', marginTop: 2 }}>
              {readiness?.explanation}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Main connection card */}
      <Paper elevation={0} style={{ ...cardStyle, marginBottom: 24 }}>
        <Grid container alignItems='center' spacing={2} style={{ marginBottom: 8 }}>
          <Grid item xs>
            <Typography variant='h6' style={{ color: '#202b3c', fontWeight: 700 }}>
              Connection details
            </Typography>
            <Typography variant='body2' style={{ color: '#687891', marginTop: 4 }}>
              Use metadata when the application supports it. Otherwise, copy the values below.
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='secondary'
              onClick={() => metadataUrl && window.open(metadataUrl, '_blank')}
              disabled={!metadataUrl}
              style={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
            >
              Download metadata XML
            </Button>
          </Grid>
        </Grid>

        <div style={{ borderTop: '1px solid #edf0f4', marginTop: 16 }} />

        {[
          {
            label: 'IdP metadata URL',
            description: 'Preferred way to share all connection details with an application',
            value: metadataUrl,
            copyLabel: 'Metadata URL',
          },
          {
            label: 'Entity ID / Issuer',
            description: 'Identifies EasyIdentity to the application',
            value: entityId,
            copyLabel: 'Entity ID / Issuer',
          },
          {
            label: 'SSO sign-in URL',
            description: 'Where the application sends users to sign in',
            value: ssoUrl,
            copyLabel: 'SSO Sign-in URL',
          },
        ].map((row, index) => (
          <div
            key={row.label}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1.2fr 160px',
              gap: 16,
              alignItems: 'center',
              padding: '14px 0',
              borderBottom: index < 2 ? '1px solid #edf0f4' : 'none',
            }}
          >
            <div>
              <Typography variant='body2' style={{ fontWeight: 700, color: '#202b3c' }}>
                {row.label}
              </Typography>
              <Typography variant='caption' style={{ color: '#687891', display: 'block', marginTop: 5 }}>
                {row.description}
              </Typography>
            </div>
            <TextField
              fullWidth
              variant='outlined'
              value={row.value || ''}
              placeholder={`[Generated ${row.label}]`}
              InputProps={{ readOnly: true, style: readonlyFieldStyle }}
            />
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => copyToClipboard(row.value, row.copyLabel)}
              disabled={!row.value}
              style={{ height: 48, textTransform: 'none', fontWeight: 600 }}
            >
              Copy
            </Button>
          </div>
        ))}

        {/* Basic details: only the stable IdP name stays in the main view. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
            paddingTop: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 260 }}>
            <Typography variant='body2' style={{ fontWeight: 700, color: '#202b3c' }}>
              IdP name
            </Typography>
            <Typography variant='body2' style={{ color: '#687891', marginTop: 5 }}>
              {selectedProvider.idp_name || '-'}
            </Typography>
          </div>

          <div style={{ flex: 1, minWidth: 320 }}>
            <Typography variant='body2' style={{ fontWeight: 700, color: '#202b3c' }}>
              Signing certificate
            </Typography>
            <Typography variant='body2' style={{ color: '#687891', marginTop: 5 }}>
              Valid until {certificateExpiry} · Status: {certificateStatus}
            </Typography>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => handleDownloadCertificate(selectedProvider)}
              disabled={!selectedProvider.certificate_download_url && !selectedProvider.x509_certificate}
              style={{ textTransform: 'none', fontWeight: 600 }}
            >
              Download certificate
            </Button>
            <Button
              variant='outlined'
              color='secondary'
              onClick={() => setShowCertificateDetails((prev) => !prev)}
              style={{ textTransform: 'none', fontWeight: 600 }}
            >
              {showCertificateDetails ? 'Hide certificate details' : 'Certificate details'}
            </Button>
          </div>
        </div>

        <Collapse in={showCertificateDetails}>
          <div style={{ borderTop: '1px solid #edf0f4', marginTop: 18, paddingTop: 18 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Certificate status'
                  fullWidth
                  variant='outlined'
                  value={certificateStatus}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Days remaining'
                  fullWidth
                  variant='outlined'
                  value={selectedProvider.certificate_alert?.days_remaining ?? '-'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Certificate issued / generated'
                  fullWidth
                  variant='outlined'
                  value={formatDate((selectedProvider as any).certificate_generated_at || (selectedProvider as any).certificate_created_at)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Certificate valid from'
                  fullWidth
                  variant='outlined'
                  value={formatDate(selectedProvider.certificate_valid_from)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Certificate valid until'
                  fullWidth
                  variant='outlined'
                  value={formatDate(selectedProvider.certificate_valid_until)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label='Certificate fingerprint (SHA-256)'
                  fullWidth
                  variant='outlined'
                  value={selectedProvider.certificate_fingerprint_sha256 || '-'}
                  InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
              </Grid>
              {certificateThresholds.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant='subtitle2' style={{ marginBottom: 8, color: '#44536b' }}>
                    Certificate alert thresholds
                  </Typography>
                  {certificateThresholds.map((threshold, index) => (
                    <Chip
                      key={`${threshold}-${index}`}
                      label={threshold}
                      variant='outlined'
                      style={{ marginRight: 8, marginBottom: 8 }}
                    />
                  ))}
                </Grid>
              )}
              {selectedProvider.certificate_alert?.message && (
                <Grid item xs={12}>
                  <Typography variant='body2' color='textSecondary'>
                    {selectedProvider.certificate_alert.message}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label='X.509 signing certificate'
                  fullWidth
                  multiline
                  rows={7}
                  variant='outlined'
                  value={formatCertificate(selectedProvider.x509_certificate) || '-'}
                  InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
              </Grid>
            </Grid>
          </div>
        </Collapse>
      </Paper>

      {/* Active state */}
      <Paper elevation={0} style={{ ...cardStyle, marginBottom: 24, padding: '12px 22px' }}>
        <Grid container alignItems='center'>
          <Grid item xs>
            <Typography variant='h6' style={{ color: '#202b3c', fontWeight: 700 }}>
              Identity provider status
            </Typography>
            <Typography variant='body2' style={{ color: '#687891', marginTop: 4 }}>
              {isProviderEnabled
                ? 'EasyIdentity is enabled and can be used by connected SAML applications to sign users in.'
                : 'EasyIdentity is disabled and connected SAML applications cannot use it to sign users in.'}
            </Typography>
          </Grid>
          <Grid item>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Switcher
                checked={isProviderEnabled}
                onChange={handleProviderToggle}
              />

              {/* <Switch checked={isProviderEnabled} onChange={handleProviderToggle} /> */}
              <Typography variant='body2' style={{ fontWeight: 600, color: isProviderEnabled ? '#16865e' : '#778397' }}>
                {isProviderEnabled ? 'Active' : 'Inactive'}
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Paper>

      {/* Advanced settings */}
      <Paper elevation={0} style={{ ...cardStyle, padding: 0, marginBottom: 14 }}>
        <Button style={sectionButtonStyle} onClick={() => setShowAdvancedSettings((prev) => !prev)}>
          <span>Advanced settings</span>
          <span style={{ color: '#687891', fontSize: 18 }}>{showAdvancedSettings ? '⌃' : '⌄'}</span>
        </Button>

        <Collapse in={showAdvancedSettings}>
          <div style={{ padding: '0 28px 24px' }}>
            <Typography variant='body2' style={{ color: '#687891', marginBottom: 18 }}>
              These settings control how EasyIdentity presents the SAML IdP configuration to connected applications.
            </Typography>

            <Grid container spacing={2}>
              {selectedProvider.configuration_options?.service_provider &&
                (['name_id_format', 'name_id_attribute'] as const).map((key) => {
                  const opts = selectedProvider.configuration_options?.service_provider?.[key];
                  if (!opts?.length) return null;

                  return (
                    <Grid item xs={12} sm={6} key={key}>
                      <Typography variant='subtitle2' style={{ marginBottom: 8, color: '#44536b' }}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </Typography>
                      <div>
                        {opts.map((opt, i) => (
                          <Chip
                            key={i}
                            label={getOptionLabel(opt)}
                            variant='outlined'
                            style={{ marginRight: 8, marginBottom: 8 }}
                          />
                        ))}
                      </div>
                    </Grid>
                  );
                })}

              {selectedProvider.configuration_options?.service_provider &&
                (['sign_assertion', 'require_signed_authn_requests', 'allow_idp_initiated', 'is_active'] as const).map(
                  (key) => {
                    const opts = selectedProvider.configuration_options?.service_provider?.[key];
                    if (!opts?.length) return null;

                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant='subtitle2' style={{ marginBottom: 8, color: '#44536b' }}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </Typography>
                        <div>
                          {opts.map((opt, i) => (
                            <Chip
                              key={i}
                              label={getBooleanOptionLabel(opt)}
                              color={opt?.value === true ? 'secondary' : 'default'}
                              variant='outlined'
                              style={{ marginRight: 8, marginBottom: 8 }}
                            />
                          ))}
                        </div>
                      </Grid>
                    );
                  }
                )}

              {selectedProvider.configuration_options?.fixed &&
                Object.entries(selectedProvider.configuration_options.fixed).map(([key, val]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField
                      label={key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      fullWidth
                      variant='outlined'
                      value={String(val)}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                ))}
            </Grid>
          </div>
        </Collapse>
      </Paper>

      {/* Technical details */}
      <Paper elevation={0} style={{ ...cardStyle, padding: 0 }}>
        <Button style={sectionButtonStyle} onClick={() => setShowTechnicalDetails((prev) => !prev)}>
          <span>Technical details and certificate renewal</span>
          <span style={{ color: '#687891', fontSize: 18 }}>{showTechnicalDetails ? '⌃' : '⌄'}</span>
        </Button>

        <Collapse in={showTechnicalDetails}>
          <div style={{ padding: '0 28px 26px' }}>
            <Typography variant='body2' style={{ color: '#687891', marginBottom: 18 }}>
              Technical values are shown for administrators who need to configure or troubleshoot a SAML application.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label='Protocol' fullWidth variant='outlined' value={selectedProvider.protocol || '-'} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label='Signing algorithm' fullWidth variant='outlined' value={selectedProvider.signing_algorithm || '-'} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label='Digest algorithm' fullWidth variant='outlined' value={selectedProvider.digest_algorithm || '-'} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label='Metadata URL' fullWidth variant='outlined' value={metadataUrl || '-'} InputProps={{ readOnly: true }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='subtitle2' style={{ marginBottom: 8, color: '#44536b' }}>
                  SSO bindings
                </Typography>
                {selectedProvider.sso_bindings?.length ? (
                  selectedProvider.sso_bindings.map((binding, index) => (
                    <Chip
                      key={index}
                      label={formatBinding(binding)}
                      variant='outlined'
                      style={{ marginRight: 8, marginBottom: 8 }}
                    />
                  ))
                ) : (
                  <Typography color='textSecondary'>No SSO bindings available.</Typography>
                )}
              </Grid>

              {selectedProvider.configuration_options?.common?.signing_algorithm && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label='Renewal signing algorithm'
                    fullWidth
                    variant='outlined'
                    value={regenSigningAlgo}
                    onChange={(e) => setRegenSigningAlgo(e.target.value)}
                  >
                    <MenuItem value=''>Use current algorithm</MenuItem>
                    {selectedProvider.configuration_options.common.signing_algorithm.map((opt) => (
                      <MenuItem key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {selectedProvider.configuration_options?.common?.digest_algorithm && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label='Renewal digest algorithm'
                    fullWidth
                    variant='outlined'
                    value={regenDigestAlgo}
                    onChange={(e) => setRegenDigestAlgo(e.target.value)}
                  >
                    <MenuItem value=''>Use current algorithm</MenuItem>
                    {selectedProvider.configuration_options.common.digest_algorithm.map((opt) => (
                      <MenuItem key={String(opt.value)} value={String(opt.value)}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12} sm={4}>
                <TextField
                  label='Certificate validity (days)'
                  type='number'
                  fullWidth
                  variant='outlined'
                  inputProps={{ min: 1, max: 3650 }}
                  value={validityDays}
                  onChange={(e) => setValidityDays(Number(e.target.value))}
                  helperText='Allowed range: 1–3650 days.'
                />
              </Grid>

              {selectedProvider.configuration_options?.common?.sso_bindings && (
                <Grid item xs={12}>
                  <Typography variant='subtitle2' style={{ marginBottom: 8, color: '#44536b' }}>
                    Available SSO binding options
                  </Typography>
                  {selectedProvider.configuration_options.common.sso_bindings.map((opt, i) => (
                    <Chip key={i} label={getOptionLabel(opt)} variant='outlined' style={{ marginRight: 8, marginBottom: 8 }} />
                  ))}
                </Grid>
              )}

              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  style={{
                    padding: '14px 16px',
                    background: '#fff8e7',
                    border: '1px solid #f0d58d',
                    borderRadius: 8,
                    marginTop: 4,
                  }}
                >
                  <Typography variant='body2' style={{ color: '#956600', fontWeight: 700, marginBottom: 4 }}>
                    Manage certificate renewal
                  </Typography>
                  <Typography variant='body2' style={{ color: '#806d4a' }}>
                    Regenerating creates a new SAML signing certificate. Connected Service Providers may need the new certificate before SSO can continue with the new signing key. Downloading the current certificate does not replace it.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={handleRegenerateCertificate}
                  disabled={regenerateCert.isLoading}
                  style={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                >
                  {regenerateCert.isLoading ? <CircularProgress size={20} /> : 'Regenerate certificate'}
                </Button>
              </Grid>
            </Grid>
          </div>
        </Collapse>
      </Paper>
    </>

  );
};

export default ProviderTable;
